/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Component, Injector, Input, OnInit} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {AuthService} from 'compob2b-auth';
import {BaseComponent, Cart, CartEventService, CartLocalService, CartProduct, CartRemoteService} from 'compob2b-libs';
import {Observable, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';

@Component({
    selector: 'app-cart-table-amount',
    templateUrl: './cart-table-amount.component.html',
    styleUrls: ['./cart-table-amount.component.scss'],
    standalone: false
})
export class CartTableAmountComponent extends BaseComponent implements OnInit {

  @Input() cartProduct: CartProduct;
  @Input() canEdit: boolean;

  multiplicityOrder = 1;
  multiplicityHeader = translations.packageBy;
  inputFormControl: UntypedFormControl;
  private inputSub$ = new Subject<number>();
  private cartService: Cart;

  constructor(
    readonly injector: Injector,
    private readonly cartEventService: CartEventService,
    private readonly authService: AuthService) {
    super();

    this.cartService = this.authService.isAnonymous() ?
      injector.get<Cart>(CartLocalService) :
      injector.get<Cart>(CartRemoteService);
  }

  ngOnInit(): void {

    this.inputFormControl = new UntypedFormControl();
    if (!this.canEdit) {
      this.inputFormControl.disable();
    }

    let multiplicity = 1;

    if (this.cartProduct.multiplicityOrder > 0) {
      multiplicity = this.cartProduct.multiplicityOrder;
    }

    if (multiplicity > 0) {
      this.multiplicityOrder = multiplicity;
    }

    this.inputFormControl.setValue(this.cartProduct.amount);

    this.inputSub$.pipe(
      debounceTime(600),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe),
      tap(val => this.manual(val))
    ).subscribe();

    this.getCartProduct().pipe(
      takeUntil(this.unsubscribe),
      tap(cartProduct => {
        if (cartProduct !== null && cartProduct.syncUid === this.cartProduct.syncUid) {
          this.inputFormControl.setValue(cartProduct.amount);
        }
      })
    ).subscribe();

  }

  getCartProduct(): Observable<CartProduct> {
    return this.cartEventService.getCartProduct();
  }

  increase() {
    this.changeInputValue(this.inputFormControl.value + this.multiplicityOrder);
  }

  decrease() {
    const res = this.inputFormControl.value - this.multiplicityOrder;

    if (res % this.multiplicityOrder === 0 && res > 0) {
      this.changeInputValue(res);
    }
  }

  onKeyDown(event) {
    switch (event.key) {
      case 'ArrowUp':
        this.increase();
        break;
      case 'ArrowDown':
        this.decrease();
        break;
    }
  }

  manualChange() {
    this.inputSub$.next(this.inputFormControl.value);
  }

  private manual(value: number) {
    let res = Math.ceil(value / this.multiplicityOrder) * this.multiplicityOrder;
    if (res === 0) {
      res = this.multiplicityOrder;
    }
    this.changeInputValue(res);
  }

  private changeInputValue(value: number) {
    this.inputFormControl.setValue(value);
    this.cartService.recount(this.cartProduct, this.inputFormControl.value).pipe(takeUntil(this.unsubscribe)).subscribe();
  }

}
